import React, { useEffect, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { IconChevronDown } from '@tabler/icons-react';

import { isDashboardSlug } from '../../utils/slug';
import type { MegaNavItem, MenuLink } from '../types';
import MegaNavFlyout from './MegaNavFlyout';

interface MegaNavProps {
  slug: string;
  mainNavLinks: MenuLink[];
  megaNavData?: MegaNavItem[];
}

export const MegaNav: React.FC<MegaNavProps> = ({ slug, megaNavData }) => {
  const { authentication } = useAuth();
  const isLoggedIn = authentication.state === 'authenticated';
  const [openNavItem, setOpenNavItem] = useState<string | null>(null);
  const [flyoutTopOffset, setFlyoutTopOffset] = useState(72);
  const navRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = (itemName: string) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    // Calculate the flyout top position based on where the nav actually is in the viewport
    // This accounts for any banners or other elements above the header
    if (navRef.current) {
      const rect = navRef.current.getBoundingClientRect();
      setFlyoutTopOffset(rect.bottom);
    }
    setOpenNavItem(itemName);
  };

  const handleMouseLeave = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setOpenNavItem(null);
      closeTimerRef.current = null;
    }, 400);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setOpenNavItem(null);
      }
    };

    if (openNavItem) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openNavItem]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  return (
    <Box ref={navRef} className="relative block [grid-column:1/-1] [grid-row:1/2]">
      <Row
        role="list"
        analyticsName="cosmos-mega-nav-menu-list"
        className="m-0 list-none justify-center max-md:hidden"
      >
        {megaNavData?.map((navItem) => {
          const isOpen = openNavItem === navItem.name;
          const hasSections = navItem.sections && navItem.sections.length > 0;

          return (
            <Box
              role="listitem"
              key={navItem.name}
              className="relative block"
              {...(hasSections && {
                onMouseEnter: () => handleMouseEnter(navItem.name),
                onMouseLeave: handleMouseLeave,
              })}
            >
              {hasSections ? (
                <>
                  <MegaNavLink
                    active={slug === navItem.href}
                    href={navItem.href}
                    name={navItem.name}
                    isOpen={isOpen}
                  >
                    <Text as="span" className="text-label-md text-inherit">
                      {navItem.name}
                    </Text>
                  </MegaNavLink>
                  <MegaNavFlyout
                    item={navItem}
                    isVisible={isOpen}
                    onMouseEnter={() => handleMouseEnter(navItem.name)}
                    onMouseLeave={handleMouseLeave}
                    topOffset={flyoutTopOffset}
                  />
                </>
              ) : (
                <Box
                  href={navItem.href}
                  aria-label={`View ${navItem.name}`}
                  aria-current={slug === navItem.href ? 'page' : undefined}
                  analyticsName={`cosmos-landing-page-v2-nav-link-${navItem.name}`}
                  className="inline-flex h-full items-center px-4 py-2 text-od-content-primary no-underline hover:text-od-content-hover-accent-brand"
                >
                  <Text as="span" className="text-label-md text-inherit">
                    {navItem.name}
                  </Text>
                </Box>
              )}
            </Box>
          );
        })}
        {isLoggedIn && (
          <Box role="listitem" className="relative block">
            <Box
              href="/dashboard"
              aria-label="View Dashboard"
              aria-current={isDashboardSlug(slug) ? 'page' : undefined}
              analyticsName="cosmos-mega-nav-dashboard"
              className="inline-flex h-full items-center px-4 py-2 text-od-content-primary no-underline hover:text-od-content-hover-accent-brand"
            >
              <Text as="span" className="text-label-md text-inherit">
                Dashboard
              </Text>
            </Box>
          </Box>
        )}
      </Row>
    </Box>
  );
};

const MegaNavLink: React.FC<
  PropsWithChildren<{
    href: string;
    active?: boolean;
    name: string;
    isOpen: boolean;
  }>
> = ({ children, name, active, isOpen, href }) => (
  <Box
    href={href}
    aria-label={`${name} menu`}
    aria-current={active ? 'page' : undefined}
    aria-expanded={isOpen}
    analyticsName={`cosmos-mega-nav-v2-nav-link-${name}`}
    className="inline-flex h-18 cursor-pointer flex-row items-center gap-2 px-4 py-2 leading-none font-medium text-od-content-primary no-underline hover:text-od-content-accent-brand focus:text-od-content-accent-brand focus:outline-none"
  >
    {children}
    <Row
      aria-hidden="true"
      className={cn(
        'items-center justify-center transition-transform duration-200',
        isOpen && 'rotate-180',
      )}
    >
      <IconChevronDown size={16} aria-hidden="true" />
    </Row>
  </Box>
);
